import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Eye, Play, ArrowLeft, Check, CheckCircle2, ChevronRight,
  Download, AlertTriangle, IndianRupee, CheckCheck, Gift,
  Users, FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { StatusBadge } from '../components/common/StatusBadge';
import { PageHeader } from '../components/common/PageHeader';
import { employeeService } from '../services/employee.service';
import { payrollService } from '../services/index';
import apiClient from '../services/apiClient';
import { KEYS } from '../lib/queryKeys';
import { formatINR } from '../lib/format';

const MONTHS       = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const STEPS = [
  { num:1, label:'Select & Bonuses' },
  { num:2, label:'Review Calculations' },
  { num:3, label:'Confirm & Pay' },
];

// ── Export helpers ─────────────────────────────────────────────────────────────
const triggerDownload = (content, filename, mime = 'text/csv') => {
  const blob = new Blob([content], { type: mime });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const downloadCSV = async (runId) => {
  try {
    const resp  = await apiClient.get(`/api/payroll/${runId}/export/csv`, { responseType: 'blob' });
    const cd    = resp.headers['content-disposition'] || '';
    const match = cd.match(/filename="?([^"]+)"?/);
    triggerDownload(await resp.data.text(), match?.[1] || `payroll_${runId}.csv`);
    toast.success('CSV downloaded');
  } catch { toast.error('Download failed'); }
};

const downloadExcel = async (runId) => {
  try {
    const resp  = await apiClient.get(`/api/payroll/${runId}/export/excel`, { responseType: 'blob' });
    const cd    = resp.headers['content-disposition'] || '';
    const match = cd.match(/filename="?([^"]+)"?/);
    const fname = match?.[1] || `Payroll_${runId}.xlsx`;
    const blob  = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const a     = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = fname; a.click();
    URL.revokeObjectURL(a.href);
    toast.success('Excel downloaded');
  } catch { toast.error('Failed to download Excel'); }
};

const downloadBank = async (runId, format = 'generic') => {
  try {
    const resp  = await apiClient.get(`/api/payroll/${runId}/export/bank?format=${format}`, { responseType: 'blob' });
    const cd    = resp.headers['content-disposition'] || '';
    const match = cd.match(/filename="?([^"]+)"?/);
    triggerDownload(await resp.data.text(), match?.[1] || `bank_${runId}.csv`);
    toast.success('Bank file downloaded');
  } catch { toast.error('Download failed'); }
};

// ── LOP tooltip — always uses stored values, never recalculates ───────────────
function LopCell({ r }) {
  const stored = r.lopDeduction || 0;
  if (stored <= 0) return <span>—</span>;
  const wDays    = r.workingDays || 26;
  const grossBase = r.basicPay + r.hra + r.allowances;
  const dailyRate = grossBase / wDays;
  const halfDed   = +(dailyRate * 0.5 * (r.halfDays||0)).toFixed(2);
  const lopDed    = +(dailyRate * (r.lopDays||0)).toFixed(2);
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger className="cursor-help underline decoration-dashed text-red-600 font-mono text-xs">
          −{formatINR(stored)}
        </TooltipTrigger>
        <TooltipContent className="text-xs space-y-1 p-3 max-w-[210px]">
          <div className="font-semibold text-slate-800 border-b border-slate-200 pb-1 mb-1">
            Daily rate: {formatINR(dailyRate)}<br/>
            <span className="text-[10px] text-slate-400 font-normal">({formatINR(grossBase)} ÷ {wDays} days)</span>
          </div>
          {(r.halfDays||0) > 0 && <div>Half-day ×{r.halfDays}: −{formatINR(halfDed)}</div>}
          {(r.lopDays||0)  > 0 && <div>LOP ×{r.lopDays}: −{formatINR(lopDed)}</div>}
          <div className="border-t border-slate-200 pt-1 mt-1 font-semibold text-red-600">
            Total: −{formatINR(stored)}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ── Run Detail Modal ───────────────────────────────────────────────────────────
function RunDetailModal({ runId, onClose }) {
  const qc = useQueryClient();
  const [showVariance, setShowVariance] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['payrollRun', runId],
    queryFn:  () => payrollService.getOne(runId),
    enabled:  !!runId,
  });

  const { data: varianceData, isLoading: varLoading } = useQuery({
    queryKey: ['payrollVariance', runId],
    queryFn:  () => apiClient.get(`/api/payroll/${runId}/variance`).then(r => r.data),
    enabled:  !!runId && showVariance,
  });

  const paidMut = useMutation({
    mutationFn: async () => {
      const run = data?.data;
      if (run?.status === 'draft') await payrollService.approve(runId);
      return payrollService.markPaid(runId);
    },
    onSuccess: () => {
      toast.success('Payroll marked as paid!');
      qc.invalidateQueries({ queryKey: ['payrollRuns'] });
      qc.invalidateQueries({ queryKey: ['payrollRun', runId] });
      qc.invalidateQueries({ queryKey: ['payrollRuns', 'paid-with-results'] });
      qc.invalidateQueries({ queryKey: KEYS.dashboard() });
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const run     = data?.data;
  const results = run?.employeeResults || [];
  const isPaid  = run?.status === 'paid';
  const variance = varianceData?.data;

  return (
    <Dialog open={!!runId} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <div>
            <DialogTitle className="text-lg font-semibold font-heading">
              Payroll — {run ? `${MONTHS[(run.month||1)-1]} ${run.year}` : '…'}
            </DialogTitle>
            {run && (
              <div className="flex items-center gap-3 mt-1">
                <StatusBadge status={run.status?.toUpperCase()} />
                <span className="text-xs text-slate-400">
                  {run.paidAt     ? `Paid ${new Date(run.paidAt).toLocaleDateString('en-IN')}` :
                   run.approvedAt ? `Approved ${new Date(run.approvedAt).toLocaleDateString('en-IN')}` :
                   `Created ${new Date(run.createdAt||Date.now()).toLocaleDateString('en-IN')}`}
                </span>
                {run.workingDays && (
                  <span className="text-xs text-slate-400">· {run.workingDays} working days</span>
                )}
              </div>
            )}
          </div>
        </DialogHeader>

        {isLoading ? (
          <div className="p-10 text-center text-slate-400 flex-1">Loading…</div>
        ) : run ? (
          <>
            <div className="grid grid-cols-3 gap-3 px-6 py-3 bg-slate-50 border-b border-slate-200 flex-shrink-0">
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Gross</div>
                <div className="text-lg font-bold font-mono text-slate-900">{formatINR(run.totalGrossPayroll)}</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Deductions</div>
                <div className="text-lg font-bold font-mono text-red-600">−{formatINR(run.totalDeductions)}</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-widest text-[#2E6DA4]">Net Pay</div>
                <div className="text-lg font-bold font-mono text-[#2E6DA4]">{formatINR(run.totalNetPayroll)}</div>
              </div>
            </div>

            {showVariance && (
              <div className="px-6 py-3 border-b border-slate-200 flex-shrink-0 bg-amber-50">
                {varLoading ? (
                  <div className="text-xs text-slate-500">Checking variance…</div>
                ) : variance?.alerts?.length > 0 ? (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-800">
                        {variance.alerts.length} employee{variance.alerts.length > 1 ? 's' : ''} with &gt;20% pay change vs {MONTHS[(variance.comparedTo?.month||1)-1]}
                      </span>
                    </div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {variance.alerts.filter(a => a.variancePct !== null).map((a, i) => (
                        <div key={i} className="flex items-center justify-between text-xs bg-white rounded px-3 py-1.5 border border-amber-200">
                          <span className="font-medium text-slate-900">{a.employeeName}</span>
                          <span className="text-slate-500 text-[10px] mx-2 flex-1">{a.reason}</span>
                          <span className={`font-mono font-semibold ${a.variancePct > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {a.variancePct > 0 ? '+' : ''}{a.variancePct}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-emerald-700">
                    <CheckCheck className="w-4 h-4" />
                    <span>{variance?.message || 'No significant pay changes detected'}</span>
                  </div>
                )}
              </div>
            )}

            <div className="flex-1 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr>
                    {['Employee','Dept','Eff. Days','Basic','HRA','Allow.','Gross','PF','ESI','LOP','Net Pay'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider text-right first:text-left whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {results.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#2E6DA4] to-[#1E3A5F] flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0">
                            {(r.employeeName||'').split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-slate-900 whitespace-nowrap">{r.employeeName}</div>
                            <div className="text-[10px] text-slate-400">{r.employeeCode}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">{r.department||'—'}</td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="font-semibold text-slate-900 font-mono text-xs">{(r.effectiveDays ?? r.daysPresent).toFixed(1)}</div>
                        <div className="text-[10px] text-slate-400 whitespace-nowrap">
                          {(r.daysPresent||0)>0 && `${r.daysPresent}P`}
                          {(r.halfDays||0)>0   && ` ${r.halfDays}H`}
                          {(r.paidLeaveDays||0)>0 && ` ${r.paidLeaveDays}PL`}
                          {(r.lopDays||0)>0    && ` ${r.lopDays}LOP`}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-xs">{formatINR(r.basicPay)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-xs">{formatINR(r.hra)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-xs">{formatINR(r.allowances)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-xs font-medium">{formatINR(r.grossEarnings)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-xs text-red-600">{r.pfDeduction  ? `−${formatINR(r.pfDeduction)}`  : '—'}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-xs text-red-600">{r.esiDeduction ? `−${formatINR(r.esiDeduction)}` : '—'}</td>
                      <td className="px-3 py-2.5 text-right"><LopCell r={r} /></td>
                      <td className="px-3 py-2.5 text-right font-mono text-xs font-semibold text-[#2E6DA4]">{formatINR(r.netPay)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                  <tr>
                    <td className="px-3 py-2.5 font-semibold text-slate-900 text-sm" colSpan={6}>Totals</td>
                    <td className="px-3 py-2.5 text-right font-mono text-sm font-semibold">{formatINR(run.totalGrossPayroll)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-sm text-red-600">−{formatINR(results.reduce((s,r)=>s+(r.pfDeduction||0),0))}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-sm text-red-600">−{formatINR(results.reduce((s,r)=>s+(r.esiDeduction||0),0))}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-sm text-red-600">−{formatINR(results.reduce((s,r)=>s+(r.lopDeduction||0),0))}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-sm font-bold text-[#2E6DA4]">{formatINR(run.totalNetPayroll)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="flex items-center justify-between px-6 py-3 border-t border-slate-200 bg-white flex-shrink-0 gap-2 flex-wrap">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowVariance(v => !v)} className="h-8 text-xs">
                  <AlertTriangle className="w-3.5 h-3.5 mr-1" />{showVariance ? 'Hide' : 'Variance'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadCSV(runId)} className="h-8 text-xs">
                  <Download className="w-3.5 h-3.5 mr-1" /> CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadExcel(runId)} className="h-8 text-xs">
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadBank(runId, 'generic')} className="h-8 text-xs">
                  <IndianRupee className="w-3.5 h-3.5 mr-1" /> NEFT
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadBank(runId, 'hdfc')} className="h-8 text-xs">
                  HDFC
                </Button>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} className="h-8 text-xs">Close</Button>
                {isPaid ? (
                  <div className="flex items-center gap-2 text-emerald-600 text-xs font-medium">
                    <CheckCheck className="w-4 h-4" /> Completed
                  </div>
                ) : (
                  <Button onClick={() => paidMut.mutate()} disabled={paidMut.isPending}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs">
                    <Check className="w-3.5 h-3.5 mr-1" />
                    {paidMut.isPending ? 'Processing…' : run?.status === 'approved' ? 'Mark as Paid' : 'Approve & Pay'}
                  </Button>
                )}
              </div>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// ── Payroll Wizard ─────────────────────────────────────────────────────────────
// draftRun: when user clicks "Continue" on an existing draft, we pass the run
//           object here so the wizard jumps straight to step 3.
function PayrollWizard({ onClose, draftRun = null }) {
  const qc  = useQueryClient();
  const now = new Date();

  // ── If resuming a draft, start at step 3 with that run's data ─────────────
  const [step,        setStep]       = useState(draftRun ? 3 : 1);
  const [month,       setMonth]      = useState(draftRun?.month || now.getMonth() + 1);
  const [year,        setYear]       = useState(draftRun?.year  || now.getFullYear());
  const [selectedIds, setSelectedIds]= useState([]);
  const [bonuses,     setBonuses]    = useState({});
  const [preview,     setPreview]    = useState(null);
  const [currentRunId,setRunId]      = useState(draftRun?._id || null);

  const { data: empData } = useQuery({
    queryKey: KEYS.employees({ status: 'active' }),
    queryFn:  () => employeeService.list({ status: 'active' }),
  });
  const employees = empData?.data || [];

  useEffect(() => {
    if (employees.length && selectedIds.length === 0)
      setSelectedIds(employees.map(e => e._id));
  }, [employees]);

  const toggleEmp = (id) => setSelectedIds(p => p.includes(id) ? p.filter(i => i !== id) : [...p, id]);
  const toggleAll = () => setSelectedIds(s => s.length === employees.length ? [] : employees.map(e => e._id));
  const totalBonus = Object.values(bonuses).reduce((s, v) => s + (parseFloat(v)||0), 0);

  const buildBonusPayload = () => {
    const bp = {};
    Object.entries(bonuses).forEach(([id, val]) => { const n = parseFloat(val); if (n > 0) bp[id] = n; });
    return bp;
  };

  const buildPayload = () => ({ month, year, employeeIds: selectedIds, bonuses: buildBonusPayload() });

  const calcMut = useMutation({
    mutationFn: () => payrollService.calculate(buildPayload()),
    onSuccess: (r) => { setPreview(r.data); setStep(2); },
    onError: (e) => toast.error(e.response?.data?.message || 'Calculation failed'),
  });

  const runMut = useMutation({
    mutationFn: () => payrollService.run(buildPayload()),
    onSuccess: (r) => { setRunId(r.data._id); setStep(3); },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to create run'),
  });

  const approveMut = useMutation({
    mutationFn: () => payrollService.approve(currentRunId),
    onError: (e) => toast.error(e.response?.data?.message || 'Approve failed'),
  });

  const paidMut = useMutation({
    mutationFn: () => payrollService.markPaid(currentRunId),
    onSuccess: () => {
      toast.success('Payroll marked as paid!');
      // ── Invalidate list so it refreshes when we go back ──────────────────
      qc.invalidateQueries({ queryKey: ['payrollRuns'] });
      qc.invalidateQueries({ queryKey: ['payrollRuns', 'paid-with-results'] });
      qc.invalidateQueries({ queryKey: KEYS.dashboard() });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const handleMarkPaid = async () => {
    if (!approveMut.isSuccess) await approveMut.mutateAsync();
    await paidMut.mutateAsync();
  };

  // ── Totals: prefer live preview, fall back to the draftRun data ───────────
  const results      = preview?.results     || [];
  const totals       = preview?.totals      || (draftRun ? {
    totalGrossPayroll: draftRun.totalGrossPayroll,
    totalDeductions:   draftRun.totalDeductions,
    totalNetPayroll:   draftRun.totalNetPayroll,
  } : {});
  const previewWDays = preview?.workingDays || draftRun?.workingDays || 26;
  const isPaid       = paidMut.isSuccess;

  // ── Effective employee count for step 3 summary ───────────────────────────
  const empCount = draftRun ? draftRun.totalEmployees : selectedIds.length;

  // ── Back handler for step 3 ───────────────────────────────────────────────
  // If we resumed a draft (jumped straight to step 3), "Back" should go to
  // the list, NOT to step 2 (which was never visited).
  const handleStep3Back = () => {
    if (draftRun) {
      qc.invalidateQueries({ queryKey: ['payrollRuns'] }); // refresh list
      onClose();
    } else {
      setStep(2);
    }
  };

  return (
    <div>
      <Button variant="ghost" onClick={() => {
        qc.invalidateQueries({ queryKey: ['payrollRuns'] });
        onClose();
      }} className="h-9 mb-6">
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Payroll
      </Button>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 font-heading">Run Payroll</h1>
        <p className="text-sm text-slate-500 mt-1">Process monthly payroll in three steps.</p>
      </div>

      {/* Stepper */}
      <Card className="p-5 sm:p-6 border-slate-200 shadow-sm mb-5">
        <div className="flex items-center gap-2 sm:gap-4">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.num}>
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm transition-colors ${
                  step > s.num ? 'bg-emerald-500 text-white' :
                  step === s.num ? 'bg-[#2E6DA4] text-white ring-4 ring-[#2E6DA4]/15' :
                  'bg-slate-100 text-slate-500'}`}>
                  {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                </div>
                <div className="hidden sm:block">
                  <div className={`text-xs uppercase tracking-wider font-medium ${step >= s.num ? 'text-slate-900' : 'text-slate-400'}`}>Step {s.num}</div>
                  <div className={`text-sm font-medium ${step >= s.num ? 'text-slate-900' : 'text-slate-400'}`}>{s.label}</div>
                </div>
              </div>
              {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 transition-colors ${step > s.num ? 'bg-[#2E6DA4]' : 'bg-slate-200'}`} />}
            </React.Fragment>
          ))}
        </div>
      </Card>

      {/* ── Step 1 ── */}
      {step === 1 && (
        <Card className="border-slate-200 shadow-sm overflow-hidden">
          <div className="p-5 sm:p-6 border-b border-slate-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-600">Month</Label>
                <Select value={String(month)} onValueChange={v => setMonth(Number(v))}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-600">Year</Label>
                <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>{[2023,2024,2025,2026].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between bg-slate-50 px-4 py-3 border-b border-slate-200">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <Checkbox checked={selectedIds.length === employees.length && employees.length > 0} onCheckedChange={toggleAll} />
                <span className="text-sm font-medium text-slate-900">Select all ({employees.length})</span>
              </label>
              {totalBonus > 0 && (
                <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                  <Gift className="w-3.5 h-3.5" /> Total bonus: {formatINR(totalBonus)}
                </span>
              )}
            </div>
            <div className="grid grid-cols-12 items-center px-4 py-2 bg-slate-50 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-400 font-medium">
              <div className="col-span-1" />
              <div className="col-span-5">Employee</div>
              <div className="col-span-3 text-right">Gross Salary</div>
              <div className="col-span-3 text-right flex items-center justify-end gap-1"><Gift className="w-3 h-3" /> Bonus (₹)</div>
            </div>
            <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
              {employees.map(emp => (
                <div key={emp._id} className="grid grid-cols-12 items-center px-4 py-2.5 hover:bg-slate-50">
                  <div className="col-span-1"><Checkbox checked={selectedIds.includes(emp._id)} onCheckedChange={() => toggleEmp(emp._id)} /></div>
                  <div className="col-span-5 flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#2E6DA4] to-[#1E3A5F] flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0">
                      {emp.name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{emp.name}</div>
                      <div className="text-xs text-slate-400 truncate">{emp.department}</div>
                    </div>
                  </div>
                  <div className="col-span-3 text-right text-sm font-mono text-slate-700">
                    {formatINR(emp.salaryStructure?.grossFixed || (emp.salaryStructure?.basic||0)+(emp.salaryStructure?.hra||0)+(emp.salaryStructure?.allowances||0))}
                  </div>
                  <div className="col-span-3 flex justify-end">
                    <Input type="number" min="0" placeholder="0" value={bonuses[emp._id]||''}
                      onChange={e => setBonuses(p => ({...p, [emp._id]: e.target.value}))}
                      className="h-7 w-24 text-right text-sm font-mono"
                      disabled={!selectedIds.includes(emp._id)} />
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2.5 bg-emerald-50 border-t border-emerald-100 flex items-center gap-2">
              <Gift className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
              <span className="text-xs text-emerald-700">Festival / performance bonuses included in gross, subject to ESI if applicable.</span>
            </div>
          </div>
          <div className="flex justify-end p-5 border-t border-slate-200">
            <Button onClick={() => calcMut.mutate()} disabled={selectedIds.length === 0 || calcMut.isPending}
              className="bg-[#2E6DA4] hover:bg-[#255a88] text-white">
              {calcMut.isPending ? 'Calculating…' : <>Calculate <ChevronRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </div>
        </Card>
      )}

      {/* ── Step 2 ── */}
      {step === 2 && (
        <Card className="border-slate-200 shadow-sm overflow-hidden">
          <div className="p-5 sm:p-6 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-slate-900 font-heading">
                Salary calculations · {MONTHS[month-1]} {year}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">Based on {previewWDays} working days</p>
            </div>
            {totalBonus > 0 && (
              <span className="text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full flex items-center gap-1">
                <Gift className="w-3 h-3" /> {formatINR(totalBonus)} bonuses
              </span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {['Employee','Eff. Days','Basic','HRA','Allow.','Bonus','Gross','PF','ESI','LOP','Net Pay'].map(h => (
                    <th key={h} className="px-3 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-right first:text-left">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm font-mono">
                {results.map(r => (
                  <tr key={r.employeeId} className="hover:bg-slate-50">
                    <td className="px-3 py-2.5 font-sans text-slate-900 font-medium whitespace-nowrap">{r.employeeName}</td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="font-semibold text-slate-900">{(r.effectiveDays??r.daysPresent).toFixed(1)}</div>
                      <div className="text-[10px] text-slate-400 font-sans">
                        {(r.daysPresent||0)>0&&`${r.daysPresent}P`}
                        {(r.halfDays||0)>0&&` ${r.halfDays}H`}
                        {(r.paidLeaveDays||0)>0&&` ${r.paidLeaveDays}PL`}
                        {(r.lopDays||0)>0&&` ${r.lopDays}LOP`}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right">{formatINR(r.basicPay)}</td>
                    <td className="px-3 py-2.5 text-right">{formatINR(r.hra)}</td>
                    <td className="px-3 py-2.5 text-right">{formatINR(r.allowances)}</td>
                    <td className="px-3 py-2.5 text-right text-emerald-600">{r.bonus?formatINR(r.bonus):'—'}</td>
                    <td className="px-3 py-2.5 text-right font-medium">{formatINR(r.grossEarnings)}</td>
                    <td className="px-3 py-2.5 text-right text-red-600">{r.pfDeduction?`-${formatINR(r.pfDeduction)}`:'—'}</td>
                    <td className="px-3 py-2.5 text-right text-red-600">{r.esiDeduction?`-${formatINR(r.esiDeduction)}`:'—'}</td>
                    <td className="px-3 py-2.5 text-right text-red-600">{(r.lopDeduction||0)>0?`-${formatINR(r.lopDeduction)}`:'—'}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-[#2E6DA4]">{formatINR(r.netPay)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                <tr className="font-mono text-sm">
                  <td className="px-3 py-3 font-sans font-semibold text-slate-900" colSpan={10}>Totals</td>
                  <td className="px-3 py-3 text-right font-semibold text-[#2E6DA4]">{formatINR(totals.totalNetPayroll)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="grid grid-cols-3 gap-4 p-5 bg-slate-50 border-t border-slate-200">
            <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Total Gross</div><div className="text-xl font-semibold font-mono text-slate-900 mt-1">{formatINR(totals.totalGrossPayroll)}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Total Deductions</div><div className="text-xl font-semibold font-mono text-red-600 mt-1">−{formatINR(totals.totalDeductions)}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Total Net Pay</div><div className="text-xl font-semibold font-mono text-[#2E6DA4] mt-1">{formatINR(totals.totalNetPayroll)}</div></div>
          </div>
          <div className="flex justify-between p-5 border-t border-slate-200">
            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
            <Button onClick={() => runMut.mutate()} disabled={runMut.isPending}
              className="bg-[#2E6DA4] hover:bg-[#255a88] text-white">
              {runMut.isPending ? 'Saving…' : <>Save as Draft <ChevronRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </div>
        </Card>
      )}

      {/* ── Step 3 ── */}
      {step === 3 && (
        <Card className="p-6 sm:p-10 border-slate-200 shadow-sm text-center">
          {!isPaid ? (
            <>
              <div className="w-16 h-16 rounded-full bg-[#2E6DA4]/10 text-[#2E6DA4] flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900 font-heading">Ready to disburse</h2>
              <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto">
                Once marked as paid, payslips are generated and attendance is locked.
              </p>

              <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mt-8 mb-6">
                <div className="p-4 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Period</div>
                  <div className="text-lg font-semibold text-slate-900 mt-1">{MONTHS[month-1]} {year}</div>
                </div>
                <div className="p-4 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Employees</div>
                  <div className="text-lg font-semibold text-slate-900 mt-1">{empCount}</div>
                </div>
                <div className="p-4 rounded-md bg-[#2E6DA4]/5 border border-[#2E6DA4]/20">
                  <div className="text-[10px] uppercase tracking-widest text-[#2E6DA4]">Net Payout</div>
                  <div className="text-lg font-semibold text-[#2E6DA4] mt-1 font-mono">{formatINR(totals.totalNetPayroll)}</div>
                </div>
              </div>

              {totalBonus > 0 && (
                <div className="inline-flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 mb-6">
                  <Gift className="w-3.5 h-3.5" /> Includes {formatINR(totalBonus)} in bonuses
                </div>
              )}

              <div className="flex flex-col sm:flex-row justify-center gap-3">
                {/* ── Back: go to list if resuming draft, go to step 2 if fresh run ── */}
                <Button variant="outline" onClick={handleStep3Back}>
                  <ArrowLeft className="w-4 h-4 mr-1.5" />
                  {draftRun ? 'Back to Payroll List' : 'Back'}
                </Button>
                <Button onClick={handleMarkPaid} disabled={paidMut.isPending || approveMut.isPending}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-6">
                  <Check className="w-4 h-4 mr-1.5" />
                  {paidMut.isPending || approveMut.isPending ? 'Processing…' : 'Mark All as Paid'}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900 font-heading">Payroll completed!</h2>
              <p className="text-sm text-slate-500 mt-1.5">{empCount} employees paid for {MONTHS[month-1]} {year}.</p>
              {currentRunId && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={() => downloadCSV(currentRunId)} className="h-8 text-xs">
                    <Download className="w-3 h-3 mr-1" /> CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => downloadExcel(currentRunId)} className="h-8 text-xs">
                    <FileSpreadsheet className="w-3 h-3 mr-1" /> Excel
                  </Button>
                </div>
              )}
              <Button variant="outline" onClick={() => {
                qc.invalidateQueries({ queryKey: ['payrollRuns'] });
                onClose();
              }} className="mt-4">
                Back to Payroll
              </Button>
            </>
          )}
        </Card>
      )}
    </div>
  );
}

// ── Payroll List Page ──────────────────────────────────────────────────────────
export default function Payroll() {
  const now = new Date();
  const [wizardOpen,    setWizardOpen]    = useState(false);
  const [draftToResume, setDraftToResume] = useState(null); // the draft run to continue
  const [viewRunId,     setViewRunId]     = useState(null);
  const [filterStatus,  setFilterStatus]  = useState('all');
  const [filterYear,    setFilterYear]    = useState('all');
  const [filterMonth,   setFilterMonth]   = useState('all');
  const [sortBy,        setSortBy]        = useState('date_desc');

  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['payrollRuns', {}],
    queryFn:  () => payrollService.list({}),
  });
  const runs = data?.data || [];

  const availableYears = useMemo(() =>
    [...new Set(runs.map(r => String(r.year)))].sort((a,b) => b-a),
  [runs]);

  const filtered = useMemo(() => {
    let list = runs.filter(r => {
      if (filterStatus !== 'all' && r.status !== filterStatus) return false;
      if (filterYear   !== 'all' && String(r.year) !== filterYear)   return false;
      if (filterMonth  !== 'all' && String(r.month) !== filterMonth) return false;
      return true;
    });
    list = list.slice().sort((a, b) => {
      switch(sortBy) {
        case 'date_asc':  return (a.year*100+a.month) - (b.year*100+b.month);
        case 'net_desc':  return (b.totalNetPayroll||0) - (a.totalNetPayroll||0);
        case 'net_asc':   return (a.totalNetPayroll||0) - (b.totalNetPayroll||0);
        case 'emp_desc':  return (b.totalEmployees||0) - (a.totalEmployees||0);
        default:          return (b.year*100+b.month) - (a.year*100+a.month);
      }
    });
    return list;
  }, [runs, filterStatus, filterYear, filterMonth, sortBy]);

  const ytd        = runs.filter(r => r.year === now.getFullYear() && r.status === 'paid').reduce((s,r) => s+(r.totalNetPayroll||0), 0);
  const paidRuns   = runs.filter(r => r.status === 'paid').length;
  const draftRuns  = runs.filter(r => r.status === 'draft').length;
  const filteredNet = filtered.reduce((s,r) => s+(r.totalNetPayroll||0), 0);
  const hasFilters  = filterStatus !== 'all' || filterYear !== 'all' || filterMonth !== 'all';

  // ── Open wizard: fresh run OR resume draft ────────────────────────────────
  const openWizard = (draft = null) => {
    setDraftToResume(draft);
    setWizardOpen(true);
  };

  const closeWizard = () => {
    setWizardOpen(false);
    setDraftToResume(null);
    qc.invalidateQueries({ queryKey: ['payrollRuns'] }); // always refresh list on close
  };

  if (wizardOpen) return (
    <PayrollWizard
      onClose={closeWizard}
      draftRun={draftToResume}
    />
  );

  return (
    <div>
      <PageHeader title="Payroll" description="Run, review, and analyse monthly payroll.">
        <Button onClick={() => openWizard()} className="bg-[#2E6DA4] hover:bg-[#255a88] text-white">
          <Plus className="w-4 h-4 mr-1.5" /> Run New Payroll
        </Button>
      </PageHeader>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">YTD Payout ({now.getFullYear()})</div>
          <div className="mt-2 text-2xl font-bold font-mono text-[#2E6DA4]">{isLoading ? '…' : formatINR(ytd)}</div>
          <div className="text-xs text-slate-400 mt-1">{paidRuns} paid run{paidRuns!==1?'s':''}</div>
        </Card>
        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Total Runs</div>
          <div className="mt-2 text-2xl font-bold font-mono text-slate-900">{isLoading ? '…' : runs.length}</div>
          <div className="text-xs text-slate-400 mt-1">{draftRuns} draft{draftRuns!==1?'s':''} pending</div>
        </Card>
        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Last Run</div>
          <div className="mt-2 text-lg font-semibold text-slate-900">
            {runs[0] ? `${MONTHS_SHORT[runs[0].month-1]} ${runs[0].year}` : '—'}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {runs[0] ? <StatusBadge status={runs[0].status?.toUpperCase()} /> : 'No runs yet'}
          </div>
        </Card>
        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Avg Net / Run</div>
          <div className="mt-2 text-2xl font-bold font-mono text-slate-900">
            {paidRuns > 0 ? formatINR(Math.round(ytd / paidRuns)) : '—'}
          </div>
          <div className="text-xs text-slate-400 mt-1">paid runs only</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-5 border-slate-200 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="space-y-1 min-w-[120px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Status</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 min-w-[100px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Year</Label>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {availableYears.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 min-w-[130px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Month</Label>
            <Select value={filterMonth} onValueChange={setFilterMonth}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All months</SelectItem>
                {MONTHS.map((m,i) => <SelectItem key={m} value={String(i+1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 min-w-[170px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Sort by</Label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="date_desc">Date — newest first</SelectItem>
                <SelectItem value="date_asc">Date — oldest first</SelectItem>
                <SelectItem value="net_desc">Net pay — highest first</SelectItem>
                <SelectItem value="net_asc">Net pay — lowest first</SelectItem>
                <SelectItem value="emp_desc">Employees — most first</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {hasFilters && (
            <Button variant="outline" size="sm" className="h-9 shrink-0"
              onClick={() => { setFilterStatus('all'); setFilterYear('all'); setFilterMonth('all'); setSortBy('date_desc'); }}>
              Reset
            </Button>
          )}
        </div>
        {filtered.length > 0 && (
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
            <span>{filtered.length} run{filtered.length!==1?'s':''}</span>
            <span className="text-slate-300">|</span>
            <span>Total net: <span className="font-mono font-semibold text-[#2E6DA4]">{formatINR(filteredNet)}</span></span>
          </div>
        )}
      </Card>

      {/* Runs table */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-900 font-heading">Payroll History</h3>
          <span className="text-xs text-slate-400">{filtered.length} of {runs.length} runs</span>
        </div>
        {isLoading ? (
          <div className="p-10 text-center text-slate-400 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm">
            {runs.length === 0 ? 'No payroll runs yet.' : 'No runs match the current filters.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {['Period','Employees','Gross','Deductions','Net Payout','Status','Actions'].map(h => (
                    <th key={h} className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-left last:text-right">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(run => (
                  <tr key={run._id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="text-sm font-semibold text-slate-900">{MONTHS[(run.month||1)-1]} {run.year}</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {run.paidAt     ? `Paid ${new Date(run.paidAt).toLocaleDateString('en-IN')}` :
                         run.approvedAt ? `Approved ${new Date(run.approvedAt).toLocaleDateString('en-IN')}` :
                         run.createdAt  ? `Created ${new Date(run.createdAt).toLocaleDateString('en-IN')}` : ''}
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1.5 text-sm text-slate-700">
                        <Users className="w-3.5 h-3.5 text-slate-400" />
                        {run.totalEmployees || '—'}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-sm font-mono text-slate-700">{formatINR(run.totalGrossPayroll)}</td>
                    <td className="px-5 py-3.5 text-sm font-mono text-red-600">−{formatINR(run.totalDeductions)}</td>
                    <td className="px-5 py-3.5 text-sm font-mono font-semibold text-[#2E6DA4]">{formatINR(run.totalNetPayroll)}</td>
                    <td className="px-5 py-3.5"><StatusBadge status={run.status?.toUpperCase()} /></td>
                    <td className="px-5 py-3.5">
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => setViewRunId(run._id)} className="h-8">
                          <Eye className="w-3.5 h-3.5 mr-1" /> View
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => downloadExcel(run._id)} className="h-8" title="Download Excel">
                          <FileSpreadsheet className="w-3.5 h-3.5" />
                        </Button>
                        {/* ── Continue: pass the draft run object, jumps to step 3 ── */}
                        {run.status === 'draft' && (
                          <Button size="sm" onClick={() => openWizard(run)}
                            className="bg-[#2E6DA4] hover:bg-[#255a88] text-white h-8">
                            <Play className="w-3.5 h-3.5 mr-1" /> Continue
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {viewRunId && <RunDetailModal runId={viewRunId} onClose={() => setViewRunId(null)} />}
    </div>
  );
}